"use client";

import { useTaskProgress } from "@/hooks/useTaskProgress";
import { AeraButton } from "@/app/components/ui/AeraButton";

interface TaskProgressWidgetProps {
    orderId: string;
    onExpand: () => void;
    onShowConfig: () => void;
    onClose: () => void;
}

export default function TaskProgressWidget({
    orderId,
    onExpand,
    onClose,
}: TaskProgressWidgetProps) {
    // Use custom hook for task progress
    const { taskData, loading: isLoading, progressValue: progress } = useTaskProgress({
        orderId,
        pollingInterval: 5000,
        autoStart: true,
        onComplete: onClose, // Auto-close when task completes
    });

    const status = taskData?.status ?? "กำลังดำเนินการ";

    return (
        <div className="fixed bottom-6 right-6 z-40">
            <div className="bg-aera-white rounded-2xl shadow-2xl border-2 border-indigo-200 overflow-hidden w-72 animate-slide-in">
                {/* Header */}
                <div className="bg-linear-to-r from-indigo-600 to-purple-600 px-4 py-3 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-aera-greendark rounded-full animate-pulse"></div>
                        <span className="text-aera-white font-semibold text-sm">สถานะงาน</span>
                    </div>
                    <AeraButton
                        variant="ghost"
                        size="xs"
                        onClick={onClose}
                        className="text-aera-white hover:bg-aera-white/20 rounded-full p-1! transition-colors h-auto! w-auto! min-w-0!"
                        aria-label="Close"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </AeraButton>
                </div>

                {/* Content */}
                <div
                    onClick={onExpand}
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center py-4">
                            <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <>
                            {/* Progress Circle */}
                            <div className="flex items-center gap-4 mb-3">
                                <div className="relative w-16 h-16 shrink-0">
                                    {/* Background circle */}
                                    <svg className="w-16 h-16 transform -rotate-90">
                                        <circle
                                            cx="32"
                                            cy="32"
                                            r="28"
                                            stroke="#e5e7eb"
                                            strokeWidth="6"
                                            fill="none"
                                        />
                                        {/* Progress circle */}
                                        <circle
                                            cx="32"
                                            cy="32"
                                            r="28"
                                            stroke="#6366f1"
                                            strokeWidth="6"
                                            fill="none"
                                            strokeDasharray={`${2 * Math.PI * 28}`}
                                            strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`}
                                            className="transition-all duration-500"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    {/* Percentage text */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="text-lg font-bold text-indigo-600">{Math.round(progress)}%</span>
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-aera-slate truncate">
                                        {taskData?.title || "กำลังประมวลผล"}
                                    </p>
                                    <p className="text-xs text-aera-slate mt-1">{status}</p>
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-linear-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>

                            {/* Steps Summary */}
                            {taskData?.steps && taskData.steps.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    <div className="flex items-center gap-2 mb-2">
                                        <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                        <span className="text-xs font-semibold text-aera-slate">ขั้นตอน</span>
                                    </div>
                                    <div className="space-y-1.5">
                                        {taskData.steps
                                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                                            .slice(0, 3)
                                            .map((step, index) => {
                                                const stepProgress = step.progress || 0;
                                                const isCompleted = stepProgress >= 100;
                                                const isInProgress = stepProgress > 0 && stepProgress < 100;

                                                return (
                                                    <div key={step.id || index} className="flex items-center gap-2">
                                                        <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${isCompleted ? 'bg-aera-greenlight/40' : isInProgress ? 'bg-blue-100' : 'bg-gray-100'
                                                            }`}>
                                                            {isCompleted ? (
                                                                <svg className="w-2.5 h-2.5 text-aera-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            ) : isInProgress ? (
                                                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                                                            ) : (
                                                                <span className="text-[8px] font-semibold text-aera-slate">{index + 1}</span>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between gap-1">
                                                                <p className="text-xs text-aera-slate truncate">
                                                                    {step.name || `ขั้นตอนที่ ${index + 1}`}
                                                                </p>
                                                                <span className="text-xs font-semibold text-indigo-600">
                                                                    {Math.round(stepProgress)}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        {taskData.steps.length > 3 && (
                                            <p className="text-xs text-aera-slate text-center pt-1">
                                                +{taskData.steps.length - 3} ขั้นตอนเพิ่มเติม
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Latest Activity */}
                            {taskData?.activities && taskData.activities.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    <div className="flex items-center gap-2 mb-2">
                                        <svg className="w-3.5 h-3.5 text-aera-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        <span className="text-xs font-semibold text-aera-slate">กิจกรรมล่าสุด</span>
                                    </div>
                                    {(() => {
                                        const latestActivity = taskData.activities[0];
                                        return (
                                            <div className="bg-linear-to-br from-aera-greenlight/20 to-aera-amber rounded-lg p-2.5 border border-aera-greenlight">
                                                {latestActivity.type && (
                                                    <span className="text-[10px] font-semibold text-aera-green uppercase tracking-wide">
                                                        {latestActivity.type}
                                                    </span>
                                                )}
                                                {latestActivity.description && (
                                                    <p className="text-xs text-aera-slate mt-1 line-clamp-2">
                                                        {latestActivity.description}
                                                    </p>
                                                )}
                                                {latestActivity.createdAt && (
                                                    <p className="text-[10px] text-aera-slate mt-1.5 flex items-center gap-1">
                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        {new Date(latestActivity.createdAt).toLocaleString('th-TH', {
                                                            dateStyle: 'short',
                                                            timeStyle: 'short'
                                                        })}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    {taskData.activities.length > 1 && (
                                        <p className="text-xs text-aera-slate text-center mt-2">
                                            +{taskData.activities.length - 1} กิจกรรมเพิ่มเติม
                                        </p>
                                    )}
                                </div>
                            )}

                            <p className="text-xs text-aera-slate mt-3 text-center">
                                คลิกเพื่อดูรายละเอียด
                            </p>
                        </>
                    )}
                </div>
            </div>

            <style jsx>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateY(100px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
        </div>
    );
}
